import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { FlightSearchParams } from '~/types';
import { trackButtonClick } from '~/utils/analytics';

type Props = {
  searchParams: FlightSearchParams;
  onEdit: () => void;
};

function FlightItinerarySummaryCard({ onEdit, searchParams }: Props) {
  const from = searchParams.from?.city || searchParams.from?.title || 'Origin';
  const to = searchParams.to?.city || searchParams.to?.title || 'Destination';
  const outbound = searchParams.departureDate
    ? dayjs(searchParams.departureDate).format('D MMM YYYY')
    : '-';
  const inbound = searchParams.returnDate
    ? dayjs(searchParams.returnDate).format('D MMM YYYY')
    : '-';
  const ticketType = searchParams.type === 'return' ? 'Return' : 'One way';
  const travelClass = searchParams.travelClass
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());
  const passengerCount =
    searchParams.passengers.adults +
    searchParams.passengers.children +
    searchParams.passengers.infants;
  const passengerSummary = [
    `${searchParams.passengers.adults} adult${searchParams.passengers.adults === 1 ? '' : 's'}`,
    searchParams.passengers.children
      ? `${searchParams.passengers.children} child${searchParams.passengers.children === 1 ? '' : 'ren'}`
      : '',
    searchParams.passengers.infants
      ? `${searchParams.passengers.infants} infant${searchParams.passengers.infants === 1 ? '' : 's'}`
      : '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Paper
      aria-label="Trip summary"
      component="section"
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
        mb: 2,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography
            component="h2"
            sx={{ fontWeight: 700, fontSize: 15, overflowWrap: 'anywhere' }}
          >
            {from}
            {searchParams.from?.iata ? ` (${searchParams.from.iata})` : ''}
            {' → '}
            {to}
            {searchParams.to?.iata ? ` (${searchParams.to.iata})` : ''}
          </Typography>
          <Chip
            label={ticketType}
            size="small"
            sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: '#edf4ff', color: '#0f4e92' }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 2,
            rowGap: 0.5,
            mt: 0.5,
            color: 'text.secondary',
            '& svg': { fontSize: 16, flexShrink: 0 },
            '& > p': { display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12 },
          }}
        >
          <Typography
            aria-label={`Outbound: ${outbound}${searchParams.type === 'return' ? `, Inbound: ${inbound}` : ''}`}
          >
            <CalendarMonthIcon />
            {outbound}
            {searchParams.type === 'return' ? ` – ${inbound}` : ''}
          </Typography>
          <Typography>
            <AirlineSeatReclineNormalIcon />
            {travelClass}
          </Typography>
          <Typography
            aria-label={`${passengerCount} passengers (${passengerSummary})`}
            title={`${passengerCount} passengers`}
          >
            <PeopleAltIcon />
            {passengerSummary}
          </Typography>
        </Box>
      </Box>
      <Button
        aria-label="Edit trip summary"
        onClick={() => {
          trackButtonClick('FlightItinerarySummaryCard', 'Edit trip summary');
          onEdit();
        }}
        size="small"
        startIcon={<EditOutlinedIcon />}
        sx={{
          minHeight: 44,
          flexShrink: 0,
          textTransform: 'none',
          '&&': { color: '#0f4e92', bgcolor: 'transparent' },
          '&& .MuiButton-startIcon': { color: '#0f4e92' },
          '&&:hover': { bgcolor: '#edf4ff' },
          '&:focus-visible': { outline: '2px solid #0f4e92', outlineOffset: 2 },
        }}
        variant="text"
      >
        Edit
      </Button>
    </Paper>
  );
}

export default FlightItinerarySummaryCard;
